import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Grow from "@mui/material/Grow";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import { styled, SxProps, type Theme } from "@mui/material/styles";
import type {} from "@mui/material/themeCssVarsAugmentation";
import * as React from "react";
import { Link } from "../Common/Link";
import type {
  Navigation,
  NavigationPageItem,
} from "../useNavigation/navigation";
import { MINI_DRAWER_WIDTH } from "./shared";

const NavigationListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: 8,
  "&.Mui-selected": {
    "& .MuiListItemIcon-root": {
      color: (theme.vars ?? theme).palette.primary.dark,
    },
    "& .MuiTypography-root": {
      color: (theme.vars ?? theme).palette.primary.dark,
    },
    "& .MuiSvgIcon-root": {
      color: (theme.vars ?? theme).palette.primary.dark,
    },
    "& .MuiAvatar-root": {
      backgroundColor: (theme.vars ?? theme).palette.primary.dark,
    },
    "& .MuiTouchRipple-child": {
      backgroundColor: (theme.vars ?? theme).palette.primary.dark,
    },
  },
  "& .MuiSvgIcon-root": {
    color: (theme.vars ?? theme).palette.action.active,
  },
  "& .MuiAvatar-root": {
    backgroundColor: (theme.vars ?? theme).palette.action.active,
  },
}));

export interface DashboardSidebarItemProps {
  /**
   * A string that uniquely identifies the item.
   */
  id: string;
  /**
   * Navigation item definition.
   */
  item: NavigationPageItem;
  /**
   * Callback fired when the item is clicked.
   */
  onClick: (itemId: string, item: NavigationPageItem) => void;
  /**
   * Item title.
   */
  title: string;
  /**
   * Link `href` for when the item is rendered as a link.
   */
  href: string;
  /**
   * The component used to render the item as a link.
   */
  Link?: React.ElementType;
  /**
   * If `true`, expands any nested navigation in the item, otherwise collapse it.
   * @default false
   */
  expanded?: boolean;
  /**
   * If `true`, the containing sidebar is in mini mode.
   * @default false
   */
  mini?: boolean;
  /**
   * Use to apply selected styling.
   * @default false
   */
  selected?: boolean;
  /**
   * If `true`, the item is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * If `true`, the containing sidebar is fully expanded.
   * @default true
   */
  isSidebarFullyExpanded?: boolean;
  /**
   * If `true`, the containing sidebar is fully collapsed.
   * @default false
   */
  isSidebarFullyCollapsed?: boolean;
  /**
   * Override the component rendered as nested navigation for this item.
   */
  renderNestedNavigation: (subNavigation: Navigation) => React.ReactNode;
}

const LIST_ITEM_ICON_SIZE = 24;
function DashboardSidebarItem({
  id,
  item,
  onClick,
  title,
  href,
  Link: LinkProp,
  expanded = false,
  mini = false,
  selected = false,
  disabled = false,
  isSidebarFullyExpanded = true,
  isSidebarFullyCollapsed = false,
  renderNestedNavigation,
}: DashboardSidebarItemProps) {
  const [hoveredMiniSidebarItemId, setHoveredMiniSidebarItemId] =
    React.useState<string | null>(null);

  const handleClick = React.useCallback(() => {
    onClick(id, item);
  }, [id, item, onClick]);

  let nestedNavigationCollapseSx: SxProps<Theme> = { display: "none" };
  if (mini && isSidebarFullyCollapsed) {
    nestedNavigationCollapseSx = {
      fontSize: 18,
      position: "absolute",
      top: "41.5%",
      right: "2px",
      transform: "translateY(-50%) rotate(-90deg)",
    };
  } else if (!mini && isSidebarFullyExpanded) {
    nestedNavigationCollapseSx = {
      ml: 0.5,
      transform: `rotate(${expanded ? 0 : -90}deg)`,
      transition: (theme: Theme) =>
        theme.transitions.create("transform", {
          easing: theme.transitions.easing.sharp,
          duration: 100,
        }),
    };
  }

  const hasExternalHref =
    href.startsWith("http://") || href.startsWith("https://");

  const LinkComponent = LinkProp ?? (hasExternalHref ? "a" : Link);

  const listItem = (
    <ListItem
      {...(item.children && mini
        ? {
            onMouseEnter: () => {
              setHoveredMiniSidebarItemId(id);
            },
            onMouseLeave: () => {
              setHoveredMiniSidebarItemId(null);
            },
          }
        : {})}
      sx={{
        py: 0,
        px: 1,
        overflowX: "hidden",
      }}
    >
      <NavigationListItemButton
        selected={selected}
        disabled={disabled}
        sx={{
          px: 1.4,
          height: 48,
        }}
        {...(item.children && !mini
          ? {
              onClick: handleClick,
            }
          : {})}
        {...(!item.children
          ? {
              LinkComponent,
              ...(hasExternalHref
                ? {
                    target: "_blank",
                    rel: "noopener noreferrer",
                  }
                : {}),
              href,
              onClick: handleClick,
            }
          : {})}
      >
        {item.icon || mini ? (
          <Box>
            <ListItemIcon
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: LIST_ITEM_ICON_SIZE,
              }}
            >
              {item.icon ?? null}
              {!item.icon && mini ? (
                <Avatar
                  sx={{
                    width: LIST_ITEM_ICON_SIZE - 7,
                    height: LIST_ITEM_ICON_SIZE - 7,
                    fontSize: 12,
                    ml: "-2px",
                  }}
                >
                  {title
                    .split(" ")
                    .slice(0, 2)
                    .map((titleWord) => titleWord.charAt(0).toUpperCase())}
                </Avatar>
              ) : null}
            </ListItemIcon>
          </Box>
        ) : null}
        {!mini ? (
          <ListItemText
            primary={title}
            sx={{
              ml: 1.2,
              whiteSpace: "nowrap",
              zIndex: 1,
            }}
          />
        ) : null}
        {item.action && !mini && isSidebarFullyExpanded ? item.action : null}
        {item.children ? (
          <ExpandMoreIcon sx={nestedNavigationCollapseSx} />
        ) : null}
      </NavigationListItemButton>
      {item.children && mini ? (
        <Grow in={id === hoveredMiniSidebarItemId}>
          <Box
            sx={{
              position: "fixed",
              left: MINI_DRAWER_WIDTH - 2,
              pl: "6px",
            }}
          >
            <Paper
              sx={{
                pt: 0.5,
                pb: 0.5,
                transform: "translateY(calc(50% - 30px))",
              }}
            >
              {renderNestedNavigation(item.children)}
            </Paper>
          </Box>
        </Grow>
      ) : null}
    </ListItem>
  );

  return (
    <React.Fragment key={id}>
      {listItem}
      {item.children && !mini ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {renderNestedNavigation(item.children)}
        </Collapse>
      ) : null}
    </React.Fragment>
  );
}

export { DashboardSidebarItem };
